/**
 * Réglages « Récompenses » de l'entreprise (réutilisabilité — Étape 9).
 * Réservé ORG/CSL/COO/ADM. Choix du mode par défaut (Virtuel / Cadeaux réels),
 * taux d'équivalence, et zone URSSAF bloquante quand on passe en cadeaux.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { bizCoinsApi, type BizSettingsData } from "../lib/api";
import { RewardsModeSelector, type RewardsModeValue } from "./RewardsModeSelector";
import { UrssafNotice } from "./UrssafNotice";
import { GiftCatalogEditor } from "./GiftCatalogEditor";
import { Button } from "./ui/button";

export function BizRewardsCard() {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<BizSettingsData | null>(null);
  const [mode, setMode] = useState<RewardsModeValue>("virtual");
  const [valueCents, setValueCents] = useState("0");
  const [ackChecked, setAckChecked] = useState(false);
  const [ackRequired, setAckRequired] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    bizCoinsApi.getSettings().then((s) => {
      setSettings(s);
      setMode(s.rewardsMode);
      setValueCents(String(s.bizcoinValueCents ?? 0));
    }).catch(() => setSettings(null));
  }, []);

  if (!settings) return null;
  const isCatalog = mode === "catalog";
  const alreadyAck = !!settings.ursaffAckAt;

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const res = await bizCoinsApi.saveSettings({
        rewardsMode: isCatalog ? "catalog" : "virtual",
        bizcoinValueCents: Number(valueCents) || 0,
        ...(ackChecked ? { ursaffAck: true } : {}),
      });
      if ("ursaffRequired" in res) {
        setAckRequired(true); // l'utilisateur doit cocher l'acquittement
      } else {
        setSettings(res);
        setAckRequired(false);
        setSaved(true);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border bg-white p-4 md:p-5 shadow-sm" style={{ borderColor: "#E4E7EF" }}>
      <h3 className="text-base font-bold mb-1" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>
        {t("bizRewards.title")}
      </h3>
      <p className="text-sm text-gray-600 mb-4">{t("bizRewards.subtitle")}</p>

      <RewardsModeSelector value={mode} onChange={(v) => { setMode(v); setSaved(false); }} />

      {isCatalog && (
        <div className="mt-4 space-y-4">
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: "#9AA0B0" }}>{t("bizRewards.equivalence")}</label>
            <div className="flex items-center gap-2">
              <span className="text-sm" style={{ color: "#384872" }}>1 BizCoin =</span>
              <input
                type="number" min={0} step={1}
                className="w-24 rounded-lg border px-2 py-1.5 text-sm"
                style={{ borderColor: "#E4E7EF", color: "#384872" }}
                value={valueCents}
                onChange={(e) => { setValueCents(e.target.value); setSaved(false); }}
              />
              <span className="text-sm" style={{ color: "#9AA0B0" }}>{t("bizRewards.cents")}</span>
            </div>
          </div>

          <UrssafNotice
            acknowledged={alreadyAck}
            ackAt={settings.ursaffAckAt}
            checked={ackChecked}
            onAcknowledge={(c) => { setAckChecked(c); setSaved(false); }}
          />
          {ackRequired && !ackChecked && !alreadyAck && (
            <p className="text-xs font-medium" style={{ color: "#C0423F" }}>{t("urssaf.required")}</p>
          )}

          {/* Catalogue éditable — une fois le mode cadeaux réellement enregistré. */}
          {settings.rewardsMode === "catalog" && (
            <div className="pt-2 border-t" style={{ borderColor: "#E4E7EF" }}>
              <GiftCatalogEditor />
            </div>
          )}
        </div>
      )}

      <div className="mt-4 flex items-center gap-3">
        <Button onClick={save} disabled={saving} className="text-white" style={{ background: "#5A4BBE" }}>
          {saving ? t("common.saving") : t("common.save")}
        </Button>
        {saved && <span className="text-sm font-medium" style={{ color: "#1E7A3C" }}>{t("common.saved")}</span>}
      </div>
    </div>
  );
}
